import { NOC_STAGES, type NocStageOrganization } from "@seagames/contracts";
import { type ColumnDef, DataTable, Select, type SelectOption } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type StageChange, ConfirmStageModal } from "../../components/noc-stages/confirm-stage-modal";
import { nocStagesOptions } from "../../lib/api/noc-stages";
import { errorMessage } from "../../lib/api/messages";
import { requireAdminRole } from "../../lib/auth/guards";
import { formatDateTime, formatShort } from "../../lib/format/datetime";

/** 11 NOCs: one page holds them all; the pager appears only if that ever stops being true. */
const PAGE_SIZE = 100;

export const Route = createFileRoute("/_app/settings/noc-stages")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: z.object({ page: z.coerce.number().int().min(1).optional().catch(undefined) }),
	component: NocStagesPage,
});

type ColumnKey = "noc" | "stage" | "override";
const FOLLOW = "follow";

/**
 * Game Settings › Sector Management (M7c NOC stages; since 0040 the stages
 * ARE the sidebar's four sectors, and every label says "Sector"). The
 * organising committee opens the GMS to NOCs sector by sector: one current
 * sector for every NOC, and per-NOC exceptions ahead or held back.
 * SUPER_ADMIN moves them (each move asks for a reason, the audit headline);
 * ADMIN reads. Kept deliberately small: four sector cards, one table, one
 * confirm.
 */
function NocStagesPage() {
	const { t } = useTranslation("nocStages");
	const { session } = Route.useRouteContext();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const isSuper = session.active_role === "SUPER_ADMIN";
	const page = search.page ?? 1;
	const overview = useQuery(nocStagesOptions({ page, page_size: PAGE_SIZE }));
	const data = overview.data;
	const current = data?.current_stage ?? null;
	const [change, setChange] = useState<StageChange | null>(null);

	// How many NOCs each stage holds, from the page on screen (all of them at 11 NOCs).
	const atStage = useMemo(() => {
		const counts = new Map<number, number>();
		for (const o of data?.organizations ?? []) counts.set(o.effective_stage, (counts.get(o.effective_stage) ?? 0) + 1);
		return counts;
	}, [data]);

	const overrideOptions: SelectOption[] = useMemo(
		() => [
			{ value: FOLLOW, label: current === null ? t("table.followAllPlain") : t("table.followAll", { n: current }) },
			...NOC_STAGES.map((s) => ({ value: String(s.stage), label: t("table.onlyStage", { n: s.stage, stage: t(`stageName.${s.key}`) }) })),
		],
		[current, t],
	);

	const columns: ColumnDef<NocStageOrganization, ColumnKey>[] = [
		{
			key: "noc",
			header: t("table.noc"),
			lockVisible: true,
			minWidth: "14rem",
			cell: (r) => (
				<span className="flex min-w-0 items-baseline gap-[0.5rem]">
					<span className="truncate font-semibold text-gms-navy-ink">{r.organization.name}</span>
					{r.organization.code && <span className="shrink-0 text-[0.78125rem] text-gms-ink-3">{r.organization.code}</span>}
				</span>
			),
		},
		{
			key: "stage",
			header: t("table.stage"),
			minWidth: "11rem",
			cell: (r) => {
				const s = NOC_STAGES.find((x) => x.stage === r.effective_stage);
				return (
					<span className="flex flex-wrap items-center gap-[0.375rem]">
						<span className="font-medium text-gms-navy-ink">{t("stageLabel", { n: r.effective_stage })}</span>
						{s && <span className="text-gms-ink-3">{t(`stageName.${s.key}`)}</span>}
						{r.override_stage !== null && (
							<span className="rounded-full bg-gms-warn-soft px-[0.5rem] py-[0.0625rem] text-[0.71875rem] font-semibold text-gms-warn">
								{t("table.exception")}
							</span>
						)}
					</span>
				);
			},
		},
		{
			key: "override",
			header: t("table.override"),
			width: "16rem",
			minWidth: "14rem",
			cell: (r) => (
				<Select
					size="sm"
					searchable={false}
					options={overrideOptions}
					value={r.override_stage === null ? FOLLOW : String(r.override_stage)}
					disabled={!isSuper || current === null}
					aria-label={t("table.overrideFor", { org: r.organization.name })}
					onChange={(v) => {
						if (v === null || current === null) return;
						const next = v === FOLLOW ? null : Number(v);
						if (next === r.override_stage) return;
						setChange({ kind: "org", organizationId: r.organization.id, organizationName: r.organization.name, stage: next, currentStage: current });
					}}
				/>
			),
		},
	];

	return (
		<div className="grid gap-[1.25rem]">
			<p className="max-w-[48rem] text-[0.875rem] leading-[1.55] text-gms-ink-2">
				{t("page.intro")}
				{!isSuper && <span className="block text-gms-ink-3">{t("page.readOnly")}</span>}
			</p>

			<ol className="grid gap-[0.75rem] md:grid-cols-2 xl:grid-cols-4" aria-label={t("page.stagesLabel")}>
				{NOC_STAGES.map((s) => {
					const isCurrent = s.stage === current;
					const count = atStage.get(s.stage) ?? 0;
					return (
						<li
							key={s.stage}
							aria-current={isCurrent ? "step" : undefined}
							className={`flex flex-col gap-[0.625rem] rounded-box border bg-base-100 px-[1rem] py-[0.875rem] ${
								isCurrent ? "border-gms-navy shadow-[inset_0_3px_0_var(--color-gms-navy)]" : "border-gms-line"
							}`}
						>
							<div className="flex items-start justify-between gap-[0.5rem]">
								<div className="grid leading-tight">
									<span className="text-[0.78125rem] font-semibold text-gms-ink-3">{t("stageLabel", { n: s.stage })}</span>
									<b className="text-[1rem] font-bold text-gms-navy-ink">{t(`stageName.${s.key}`)}</b>
								</div>
								{isCurrent && (
									<span className="shrink-0 rounded-full bg-gms-navy px-[0.5rem] py-[0.125rem] text-[0.71875rem] font-semibold text-white">
										{t("card.current")}
									</span>
								)}
							</div>
							<div className="grid gap-[0.375rem]">
								<span className="text-[0.78125rem] text-gms-ink-3">{s.stage === 1 ? t("card.opens") : t("card.adds", { n: s.stage - 1 })}</span>
								<ul className="flex flex-wrap gap-[0.25rem]">
									{s.modules.map((m) => (
										<li key={m} className="rounded-[0.375rem] bg-gms-soft-2 px-[0.5rem] py-[0.125rem] text-[0.8125rem] text-gms-navy-ink">
											{t(`modules.${m}`)}
										</li>
									))}
								</ul>
							</div>
							<div className="mt-auto flex flex-wrap items-center justify-between gap-[0.5rem] pt-[0.25rem]">
								<span className="text-[0.8125rem] text-gms-ink-2">{data ? t("card.nocCount", { count }) : " "}</span>
								{isSuper && !isCurrent && current !== null && (
									<button type="button" className="btn btn-line btn-sm" onClick={() => setChange({ kind: "all", stage: s.stage })}>
										{t("card.moveAll")}
									</button>
								)}
							</div>
						</li>
					);
				})}
			</ol>

			<DataTable<NocStageOrganization, ColumnKey>
				variant="list"
				size="sm"
				title={t("table.title")}
				description={data?.updated_at ? <span title={formatDateTime(data.updated_at)}>{t("table.changed", { when: formatShort(data.updated_at) })}</span> : undefined}
				columns={columns}
				rows={data?.organizations ?? []}
				rowKey={(r) => r.organization.id}
				loading={overview.isPending}
				error={overview.isError && !data ? { message: errorMessage(overview.error), onRetry: () => void overview.refetch() } : null}
				empty={{ title: t("table.emptyTitle"), description: t("table.emptyHint") }}
				pagination={
					data && data.total > PAGE_SIZE
						? { page, pageSize: PAGE_SIZE, total: data.total, onPageChange: (p) => void navigate({ search: { page: p }, replace: true }) }
						: undefined
				}
			/>

			<ConfirmStageModal change={change} onClose={() => setChange(null)} />
		</div>
	);
}
